<template>
  <div class="w-full h-full overflow-hidden flex relative" ref="viewer_page"
      :data-viewer-loading="String(isLoading)"
      :data-viewer-ready="String(!isLoading && !showMsgWithoutDesign)"
      :data-viewer-has-design="String(!showMsgWithoutDesign)"
      :data-viewer-binary-missing="String(binaryMissing)"
      :data-viewer-screenshot-ready="String(screenshot !== null)"
      :data-viewer-screenshot-error="String(screenshotError !== null)"
      :data-viewer-save-status-screenshot="saveStatusScreenshot"
      :data-viewer-pictogram-count="String(pictogramsInPowerPlant.length)"
  >
      <!-- La barra superior se eliminó: sus controles pasaron a overlays flotantes
            (☰ menú, toolbar central, acciones arriba-derecha). -->

      <!-- Área del visor (lienzo + todos los controles flotantes). Al FIJAR el panel
           lateral (pin), esta zona se encoge (flex) y el panel se acopla al lado en vez
           de solaparse; todos los overlays quedan confinados aquí automáticamente. -->
      <div class="epc-stage-area">

      <!-- Panel de propiedades vertical a la izquierda. Reemplaza
            a la barra horizontal de opciones. Muestra propiedades de Vector o Texto
            según lo activo; el padre enruta cada cambio al dibujo nuevo o al elemento
            seleccionado y llama al motor por sus watchers. -->
      <ViewerPropertiesPanel v-if="propsMode"
        :mode="propsMode"
        :editing="propsEditing"
        :stroke-color="activeStrokeColor"
        :fill-color="activeFillColor"
        :stroke-width="activeStrokeWidth"
        :stroke-style="activeStrokeStyle"
        :edges="activeEdges"
        :opacity="activeOpacity"
        :text-color="editingTxtColor"
        :font-size="editingTxtFontSize"
        :bold="editingTxtBold"
        :italic="editingTxtItalic"
        :align="editingTxtAlign"
        @update:strokeColor="_OnUpdateStrokeColor"
        @update:fillColor="_OnUpdateFillColor"
        @update:strokeWidth="_OnUpdateStrokeWidth"
        @update:strokeStyle="_OnUpdateStrokeStyle"
        @update:edges="_OnUpdateEdges"
        @update:opacity="_OnUpdateOpacity"
        @update:textColor="_OnUpdateTextColor"
        @update:fontSize="_OnUpdateFontSize"
        @update:bold="_OnUpdateBold"
        @update:italic="_OnUpdateItalic"
        @update:align="_OnUpdateAlign"
        @z-index="_OnZIndex"
        @request-delete="_OnRequestDelete"
      />

      <!-- Lienzo: llena el contenedor raíz. Todo lo demás flota encima (absolute). -->
      <ViewerDesign ref="viewer"
        :pictograms="selectedPictograms"
        :entity-id="entityId"
        :entity-type="entityType"
        :right-inset="sidebarRightInset"
        :binary-only="binaryOnly"
        :workLayer="workLayer"
        :canActionsPictogram="canActionsPictogram"
        :show-layer-completed="showLayerCompleted"
        :design-mode="designMode"
        :pictogram-type="pictogramType"
        :show-areas="showAreas"
        :is-drawing="isDrawing"
        :selected-tool="selectedTool"
        :drawing-stroke-width="drawingStrokeWidth"
        :drawing-dash="drawingDash"
        :drawing-stroke-color="drawingStrokeColor"
        :drawing-fill-color="drawingFillColor"
        :drawing-opacity="drawingOpacity"
        :drawing-edges="drawingEdges"
        :drawing-text-color="editingTxtColor"
        :drawing-text-font-size="editingTxtFontSize"
        :drawing-text-bold="editingTxtBold"
        :drawing-text-italic="editingTxtItalic"
        :drawing-text-align="editingTxtAlign"
        :drawing-text-opacity="editingTxtOpacity"
        @epc-loaded="_OnLoaded"
        @epc-mounted="_OnMounted"
        @epc-cleared="_OnCleared"
        @epc-message="_OnMessage"
        @epc-pointerdown="_onPointerDown"
        @epc-changestatuses="_OnShowChangeStatuses"
        @epc-open-split="_OnShowFormSplit"
        @epc-isloading="(e) => { isLoading = e }"
        @epc-reload="() => { $emit('epc-reload') }"
        @epc-saving="(e) => { isLoading = e.loading; customLoaderText = e.text }"
        @epc-screenshot="(e) => { screenshot = e }"
        @epc-pictogram-dataset="_HandlePictogramDataset"
        @epc-viewer-design-mode-drawing="(e) => { isDrawing = e }"
        @epc-viewer-design-selected-tool="(e) => { selectedTool = e }"
        @epc-txt-editing="_HandleTxtEditing"
        @epc-block-editing="_HandleBlockEditing"
        @epc-selected-count="(e) => { selectedCount = e }"
        @epc-focus-group="_HandleFocusGroup"
        @epc-powerplant-info="(e) => { plantProjectName = e.projectName; plantProjectId = e.projectId }"
        @epc-hover-block="(e) => { hoveredBlockId = e }"
        @epc-binary-missing="(e) => { binaryMissing = e }"
        @epc-binary-processing="(e) => { binaryProcessing = e; if (!e) binaryProcessingOpen = false }"
        @epc-saved="_HandleSaved"
        @epc-zoom-changed="_RefreshZoom"
        @epc-remove-pictogram="_HandleRemovePictogram"
      />

      <!-- Panel izquierdo (vistas/pictogramas): ahora FLOTA sobre el lienzo. -->
      <ViewerPanelPictograms v-if="showPanelViews"
          class="absolute top-0 left-0 bottom-0 z-10"
          :pictograms="pictograms"
          :selected-pictograms="selectedPictograms"
          @epc-update:selected-pictograms="_HandleUpdateSelectedPictograms"
          @epc-view-selected="_OnClickDisplayLayerPictogram"
          @epc-close="(e) => { showPanelViews = !e }" />

      <!-- Zoom flotante abajo-izquierda + ayuda abajo-derecha. -->
      <WidgetZoom v-if="!showMsgWithoutDesign || designMode"
        :zoom-percent="zoomPercent"
        @epc-click-zoom-in="_OnClickZoom"
        @epc-click-zoom-out="_OnClickZoom"
        @epc-click-fit="_OnFitContent"
      />

      <ViewerHelpDesign v-if="!showMsgWithoutDesign"
        @epc-click-help="_HandleClickHelp"
      />

      <!-- Estado flotante abajo-centro (vista activa + nº seleccionados).
            Reemplaza la barra de estado del footer eliminado. -->
      <ViewerStatusDesign v-if="!showMsgWithoutDesign || designMode"
        :name-vista="$t(pictogramType)"
        :selected-count="selectedCount"
        :design-mode="designMode"
      />

      <!-- Botón volver (modo foco): de un bloque enfocado a la vista completa. -->
      <button v-if="focusedGroupId && !showMsgWithoutDesign" class="epc-focus-back"
        @click="_HandleUnfocus">
        <b-icon pack="fas" icon="arrow-left" size="is-small"></b-icon>
        <span>{{ $t('back') }}</span>
      </button>

      <!-- Aviso: hay una versión nueva del diseño regenerándose; se actualizará solo al
           concluir (Pusher). Botón con spinner (arriba-derecha); al pulsar muestra el
           mensaje en un popover. -->
      <template v-if="binaryProcessing && !binaryMissing && !showMsgWithoutDesign">
        <button class="epc-binary-processing-btn" :class="{ open: binaryProcessingOpen }"
          :title="$t('processing_new_design_version')"
          @click="binaryProcessingOpen = !binaryProcessingOpen">
          <b-icon pack="fas" icon="rotate" size="is-small" custom-class="fa-spin"></b-icon>
        </button>

        <template v-if="binaryProcessingOpen">
          <div class="epc-binary-processing-backdrop" @click="binaryProcessingOpen = false"></div>
          <div class="epc-binary-processing-pop">
            <b-icon pack="fas" icon="rotate" size="is-small" custom-class="fa-spin"></b-icon>
            <span>{{ $t('processing_new_design_version') }}</span>
          </div>
        </template>
      </template>

      <!-- Acciones flotantes arriba-derecha: guardar / capturar / info / atrás. -->
      <ViewerActionsDesign v-if="!showMsgWithoutDesign || designMode"
        :design-mode="designMode"
        :can-actions="canActionsPictogram"
        :sidebar-open="showSidebar"
        :sidebar-pinned="sidebarPinned"
        @epc-click-save="_HandleClickSave"
        @epc-click-screenshot="_HandleScreenshot"
        @epc-click-return="_HandleClickReturn"
        @epc-toggle-sidebar="showSidebar = !showSidebar"
      />

      <!-- Cabecera junto al menú de vista: "Parque fotovoltaico" + nombre del proyecto
           (de navData) en tamaño/color más ligero. Estilo pro en azul de la app. -->
      <div v-if="!showMsgWithoutDesign && _Allowed('show_header')" class="epc-viewer-title">
        <span class="epc-viewer-title-main">{{ $t('photovoltaic_park') }}</span>
        <span v-if="projectDisplayName" class="epc-viewer-title-sub">{{ projectDisplayName }}</span>
      </div>

      <!-- Menú ☰ flotante arriba-izquierda: vistas (capas) + modo diseño. -->
      <WidgetMenu v-if="!showMsgWithoutDesign && _Allowed('show_layer_selector')"
        :current-view="pictogramType"
        :design-mode="designMode"
        :show-areas="showAreas"
        :can-actions="canActionsPictogram"
        @epc-open-panel="_HandleOpenPanel"
        @epc-in-design-mode="_HandleInDesignMode"
      />

      <!-- Toolbar central flotante: navegación (pan/fit/fullscreen) siempre +
            herramientas de diseño solo en modo diseño. -->
      <ViewerToolbarDesign v-if="!showMsgWithoutDesign || _Allowed('show_design_mode')"
        :design-mode="designMode"
        :can-actions="canActionsPictogram"
        :pictogram-type="pictogramType"
        :show-areas="showAreas"
        :selected-tool="selectedTool"
        :is-drawing="isDrawing"
        :has-editing-block="!!editingBlock"
        :is-draggable="isDraggable"
        :navigation-mode="navigationMode"
        :fullscreen-enabled="fullscreenEnabled"
        :status-mode="statusMode"
        :lod-enabled="lodEnabled"
        :is-focused="!!focusedGroupId"
        @epc-open-panel="_HandleOpenPanel"
        @epc-select-tool="_HandleSelectTool"
        @epc-delete-selected="_OnRequestDelete"
        @epc-click-pan="_OnClickPan"
        @epc-click-fit="_OnFitContent"
        @epc-click-fullscreen="_OnClickFullScreen"
        @epc-toggle-navigation="_OnToggleNavigation"
        @epc-in-design-mode="_HandleInDesignMode"
        @epc-status-mode="_HandleStatusMode"
        @epc-toggle-lod="_HandleToggleLod"
      />

      <ViewerScreenshotPanel v-if="showPanelScreenshot"
        :workLayer="displayPictogramLayer"
        :screenshot="screenshot"
        :can-save="_Allowed('show_save_screenshot')"
        @epc-save-screenshot="_HandleSaveScreenshot"
        @epc-close="(e) => { showPanelScreenshot = !e; screenshot = null }"/>

      <LayersToolStatuses v-show="showLayerToolStatuses"
        :selected-entities="selectedEntities"
        :statuses="statuses"
        @epc-konva-changestatus="_HandleChangeStatus"
        @epc-close="(close) => showLayerToolStatuses = !close" />

      <LayersListKonva v-show="showLayerPanel"
        :layers="filterLayers"
        @toggleLayer="_OnToggleLayer"
        @toggleAll="_OnToggleAll"
        @epc-close="(close) => showLayerPanel = !close"/>

      <LayersToolSplit v-show="showLayerToolSplit"
        :selected-entities="selectedEntities"
        :statuses="statuses"
        @epc-konva-split="handleSplitEntities"
        @epc-close="(close) => showLayerToolSplit = !close" />

      <ViewerEmptyDesign v-if="showMsgWithoutDesign && !designMode"
        :can-build="_Allowed('show_design_mode')"
        @epc-build="_HandleInDesignMode(true)" />

      <!-- Modo diseño sin pictogramas: pista para cargarlos desde el menú ⋮ → Pictogramas.
           Desaparece al insertar el primero (showMsgWithoutDesign pasa a false). -->
      <ViewerDesignEmptyHint v-if="showMsgWithoutDesign && designMode"
        @epc-open-pictograms="_HandleOpenPanel('panel-views')" />

      <!-- Modo vista con diseño pero SIN binario aún (recién guardado → regenerándose): en vez
           del lienzo en blanco, overlay centrado de "procesando"; se quita al llegar el binario. -->
      <ViewerBinaryProcessing v-if="binaryMissing && !showMsgWithoutDesign && !designMode" />

      <ViewerSpinnerLoader ref="refSpinnerLoader" :is-loading="isLoading" :custom-loader-text="customLoaderText" :with-dot="true"/>

      <ViewerInsertPictogramsModal ref="refModalInsertPictograms"
        @epc-update:selected-pictograms="_HandleUpdateSelectedPictograms"
      />

      <!-- Modal de ayuda (atajos + herramientas). Se abre desde el botón ?. -->
      <epc-modal-wrapper ref="helpModal" />

      </div><!-- /epc-stage-area -->

      <!-- Panel lateral: overlay flotante o, si está fijado (pin), acoplado al lado
           (la chincheta lo fija y el área del visor se encoge). -->
      <ViewerSidebarDesign v-if="showSidebar && !showMsgWithoutDesign && _Allowed('show_side_panel')"
        :project-id="infoProjectId"
        :hovered-block-id="hoveredBlockId"
        :focused-block-id="focusedBlockId"
        :active-layer="pictogramType"
        :docked="sidebarPinned"
        @epc-close="_HandleCloseSidebar"
        @epc-pin="_HandlePinSidebar"
      />
    </div>
</template>

<script>
import ViewerDesign from "./ViewerDesign"
import Vue from "vue"
import LayersListKonva from "../../viewer-konva/LayersListKonva"
import LayersToolStatuses from "../../viewer-konva/LayersToolStatuses.vue"
import LayersToolSplit from "../../viewer-konva/LayersToolSplit.vue"
import ViewerPanelPictograms from "../components/panels/ViewerPanelPictograms.vue"
import ViewerPanelComments from "../../viewer-konva/ViewerPanelComments.vue"
import ViewerSpinnerLoader from "../../viewer-konva/ViewerSpinnerLoader.vue"
import ViewerInsertPictogramsModal from "../components/panels/ViewerInsertPictogramsModal.vue"
import ViewerStatusDesign from "../components/chrome/ViewerStatusDesign.vue"
import { WidgetMenu } from "@pictograms/shared/widgets"
import ViewerToolbarDesign from "../components/chrome/ViewerToolbarDesign.vue"
import ViewerActionsDesign from "../components/chrome/ViewerActionsDesign.vue"
import ViewerSidebarDesign from "../components/chrome/ViewerSidebarDesign.vue"
import { WidgetZoom } from "@pictograms/shared/widgets"
import ViewerHelpDesign from "../components/chrome/ViewerHelpDesign.vue"
import ViewerPropertiesPanel from "../components/panels/ViewerPropertiesPanel.vue"
import ViewerEmptyDesign from "../components/ViewerEmptyDesign.vue"
import ViewerDesignEmptyHint from "../components/ViewerDesignEmptyHint.vue"
import ViewerBinaryProcessing from "../components/ViewerBinaryProcessing.vue"
import EpcModalWrapper from "·/components/epc-modal-wrapper.vue"
import ViewerHelpDialog from "../components/ViewerHelpDialog.vue"
import ViewerScreenshotPanel from "../components/panels/ViewerScreenshotPanel.vue"

import { isFullScreenSupported, requestFullScreen, exitFullScreen } from "@pictograms/shared/helpers/ui.helpers"
import { dashTypeToArray, dashArrayToType } from "../helpers/drawing.helper"
import { filterLayersByType } from "../helpers/layer.helper"
import { filterPictogramsByIds } from "../helpers/pictogram.helper"

export default {
    name: "ViewerPageDesign",

    components: {
    ViewerDesign,
    LayersListKonva,
    LayersToolStatuses,
    LayersToolSplit,
    ViewerPanelPictograms,
    ViewerPanelComments,
    ViewerSpinnerLoader,
    ViewerInsertPictogramsModal,
    ViewerStatusDesign,
    WidgetMenu,
    ViewerToolbarDesign,
    ViewerActionsDesign,
    ViewerSidebarDesign,
    WidgetZoom,
    ViewerHelpDesign,
    ViewerPropertiesPanel,
    ViewerEmptyDesign,
    ViewerDesignEmptyHint,
    ViewerBinaryProcessing,
    EpcModalWrapper,
    ViewerScreenshotPanel
},

    props: {
      pictograms: { type: Array, default() { return [] } },
      entityId: { type: Number, required: true },
      entityType: { type: String, default: 'project' },
      workLayer: { type: String, default: null },
      canActionsPictogram: { type:Object, default() { return {} } }, // acciones que se puede realizar sobre el pictograma por defecto ninguna
      // Solo binario (sin fallback a JSON). true en la ruta del bot (solar-plant):
      // si falta el .epcp muestra mensaje claro en vez de bajar los N JSON (lento).
      binaryOnly: { type: Boolean, default: false }
    },

    data() {
      return {
        selectedPictograms: [],
        layers: null,
        filterLayers: null,
        showLayerPanel: false,
        displayPictogramLayer: '', // capa actual del pictograma que se está visualizando
        displayDefaultPictogramLayer: null, // capa por defecto del pictograma a visualizar
        fullscreenEnabled: false, // si api navegador permite full screen
        isDraggable: false,
        isDrawing: false, // Esta bandera indica si se está graficando algún shape
        selectedTool: null, // Variable que ayuda a identificar que tipo de herramiente se ha seleccionado, (Tipo String)
        drawingStrokeWidth: 0.3, // Grosor de linea para dibujo de vectores
        drawingDash: [], // Patron de linea: [] = solida, [6,3] = guiones, [2,2] = punteada
        drawingDashType: 'solid', // Tipo de linea legible: solid, dashed, dotted
        drawingStrokeColor: '#1e1e1e', // Color de trazo para el proximo vector
        drawingFillColor: 'transparent', // Color de relleno para el proximo vector
        drawingOpacity: 0.7, // Opacidad (0..1) para el proximo vector
        drawingEdges: 'sharp', // Bordes del proximo vector: 'sharp' | 'round'
        editingTxt: null, // Datos del texto en edicion { fontSize, fontStyle, fill, align }
        editingTxtFontSize: 16,
        editingTxtBold: false,
        editingTxtColor: '#1e1e1e', // Color del texto en edicion
        editingTxtAlign: 'left', // Alineado del texto en edicion
        editingTxtOpacity: 1, // Opacidad (0..1) del texto en edicion
        editingTxtItalic: false, // Cursiva del texto en edicion
        editingBlock: null, // Datos del bloque en edicion { strokeWidth, dash, fill, stroke, opacity }
        editingBlockStrokeWidth: 0.3,
        editingBlockDashType: 'solid',
        editingBlockStrokeColor: '#1e1e1e', // Color de trazo del bloque en edicion
        editingBlockFillColor: 'transparent', // Color de relleno del bloque en edicion
        editingBlockOpacity: 0.7, // Opacidad (0..1) del bloque en edicion
        editingBlockEdges: 'sharp', // Bordes del bloque en edicion: 'sharp' | 'round'

        showLayerToolStatuses: false,
        selectedEntities: [], // entidades seleccionadas para cambiar de estado enviado desde konvaviewer.vue
        statuses: [], // estados que se aplican en una capa enviado desde konvaviewer.vue

        showLayerToolSplit: false,

        showPanelViews: false,
        showLayerCompleted: false, // visualiza una capa representando las estructuras que tienen estado completado por defecto false


        isLoading: true, // Indica si esta visible el loader (inicia en true para mostrar desde el principio)
        customLoaderText: null, // texto mostrado en el loader

        designMode: false, // indica si se esta en modo diseño o en modo visualización de datos
        datasetProject: true,
        pictogramIdSelected: 0,
        showProgress: false,
        showMsgWithoutDesign: false, // bandera que permite mostrar un mensaje si no existe el diseño

        pictogramsInPowerPlant: [], // Esta variable contiene solo los Id de los pictogramas que estan en el diseño (power plant) es obtenido cuando se carga la data del power plant y los que se van añadiendo o quitando
        pictogramType: 'structures', // Bandera que determina el tipo de pictogramas que se deben cargar, por defecto son las structures
        showAreas: false, // bandera para mostrar las areas marcadas
        navigationMode: false, // candado: true = navegación (pan, sin editar), false = edición
        zoomPercent: 100, // % de zoom mostrado en el control inferior-izquierdo
        screenshot: null, // captura de pantalla generada por el visor
        screenshotError: null, // error al generar la captura (null = sin error)
        showPanelScreenshot: false, // bandera para mostrar el panel de captura
        showSidebar: false, // abre/cierra el sidebar derecho flotante (shell)
        plantProjectName: null, // nombre de proyecto desde el power plant (fallback de cabecera)
        plantProjectId: null, // id de proyecto desde el power plant (para el panel de info)
        hoveredBlockId: null, // bloque en hover en el visor → resalta su fila en el info
        sidebarPinned: false, // panel lateral fijado (acoplado, reduce el lienzo)
        statusMode: 'progress', // 'progress' | 'incidents' — qué estado se pinta
        focusedGroupId: null, // bloque enfocado (modo foco); null = vista completa
        lodEnabled: false, // LOD (overview por siluetas); por defecto OFF
        binaryMissing: false, // binary-only: el .epcp de la vista no existe (bot falla limpio)
        binaryProcessing: false, // hay una versión nueva del diseño regenerándose (binario stale)
        binaryProcessingOpen: false, // popover del aviso de "procesando" abierto
        selectedCount: 0, // conteo de elementos seleccionados en el visor
        saveStatusScreenshot: 'idle' // estado del guardado de screenshot: idle | saving | success | error
      }
    },

    computed: {
      // Tipo de panel de propiedades activo: 'vector' | 'text' | null.
      // Solo en modo diseño y cuando hay una herramienta/elemento activo.
      propsMode() {
        if (!this.designMode) return null
        if (this.isDrawing || this.editingBlock) return 'vector'
        if (this.selectedTool === 'pencil' || this.editingTxt) return 'text'
        return null
      },

      // true cuando se está editando un elemento ya existente (habilita Acciones).
      propsEditing() {
        return !!this.editingBlock || !!this.editingTxt
      },

      // Valores activos de vector: del bloque seleccionado si lo hay, si no del dibujo nuevo.
      activeStrokeColor() {
        return this.editingBlock ? this.editingBlockStrokeColor : this.drawingStrokeColor
      },
      activeFillColor() {
        return this.editingBlock ? this.editingBlockFillColor : this.drawingFillColor
      },
      activeStrokeWidth() {
        return this.editingBlock ? this.editingBlockStrokeWidth : this.drawingStrokeWidth
      },
      activeStrokeStyle() {
        return this.editingBlock ? this.editingBlockDashType : this.drawingDashType
      },
      activeOpacity() {
        if (this.propsMode === 'text') return this.editingTxtOpacity
        return this.editingBlock ? this.editingBlockOpacity : this.drawingOpacity
      },
      activeEdges() {
        return this.editingBlock ? this.editingBlockEdges : this.drawingEdges
      },

      // Franja derecha tapada por el panel lateral cuando flota abierto (no acoplado) →
      // el callout de hover lo evita mostrándose a la izquierda. Si está acoplado, el
      // lienzo ya está reducido, así que no se descuenta nada.
      sidebarRightInset() {
        return (this.showSidebar && !this.sidebarPinned && !this.showMsgWithoutDesign) ? 340 : 0
      },

      // Nombre del proyecto para la cabecera: de navData (store), con fallback al que
      // emite el power plant.
      projectDisplayName() {
        const nav = this.$store.getters['app/navData']
        return (nav && nav.project_name) || this.plantProjectName || null
      },

      // Id de PROYECTO para el panel de info (pictogramas del proyecto). En modo 'project'
      // el entityId ya es el proyecto; en 'solar-plant' el entityId es el power plant, así
      // que se usa el projectId que emite el visor al cargar el power plant.
      infoProjectId() {
        if (this.plantProjectId != null) return this.plantProjectId
        return this.entityType === 'project' ? this.entityId : null
      },

      // Id de pictograma del bloque enfocado (modo foco): se extrae del groupId
      // ("GroupPictogram_123"). Alimenta el detalle del bloque en el panel de info.
      focusedBlockId() {
        if (!this.focusedGroupId) return null
        const id = parseInt(String(this.focusedGroupId).replace('GroupPictogram_', ''), 10)
        return Number.isNaN(id) ? null : id
      },
    },

    watch: {
      displayPictogramLayer(newVal, oldVal) {
        this.showLayerCompleted = newVal == 'modules'
      },

      drawingDashType(type) {
        this.drawingDash = dashTypeToArray(type)
      },

      editingTxtFontSize(size) {
        if (this.editingTxt) {
          this.$refs.viewer.UpdateSelectedTxtFontSize(Number(size))
        }
      },

      editingTxtBold(bold) {
        if (this.editingTxt) {
          this.$refs.viewer.UpdateSelectedTxtBold(bold)
        }
      },

      editingTxtItalic(italic) {
        if (this.editingTxt) {
          this.$refs.viewer.UpdateSelectedTxtItalic(italic)
        }
      },

      editingBlockStrokeWidth(width) {
        if (this.editingBlock) {
          this.$refs.viewer.UpdateSelectedBlockStrokeWidth(Number(width))
        }
      },

      editingBlockDashType(type) {
        if (this.editingBlock) {
          this.$refs.viewer.UpdateSelectedBlockDash(dashTypeToArray(type))
        }
      },

      editingBlockStrokeColor(color) {
        if (this.editingBlock) {
          this.$refs.viewer.UpdateSelectedBlockStroke(color)
        }
      },

      editingBlockFillColor(color) {
        if (this.editingBlock) {
          this.$refs.viewer.UpdateSelectedBlockFillColor(color)
        }
      },

      editingBlockOpacity(opacity) {
        if (this.editingBlock) {
          this.$refs.viewer.UpdateSelectedBlockOpacity(Number(opacity))
        }
      },

      editingBlockEdges(edges) {
        if (this.editingBlock) {
          this.$refs.viewer.UpdateSelectedBlockEdges(edges)
        }
      },

      editingTxtColor(color) {
        if (this.editingTxt) {
          this.$refs.viewer.UpdateSelectedTxtColor(color)
        }
      },

      editingTxtAlign(align) {
        if (this.editingTxt) {
          this.$refs.viewer.UpdateSelectedTxtAlign(align)
        }
      },

      editingTxtOpacity(opacity) {
        if (this.editingTxt) {
          this.$refs.viewer.UpdateSelectedTxtOpacity(Number(opacity))
        }
      }
    },

    mounted() {
      window.addEventListener('keydown', this._OnKeydown)
    },

    beforeDestroy() {
      window.removeEventListener('keydown', this._OnKeydown)
    },

    methods: {
      _HandlePictogramDataset(pictogramId) {
        this.$emit('epc-pictogram-project-dataset', false)
        this.datasetProject = false
        this.pictogramIdSelected = parseInt(pictogramId)
      },

      _HandleClickReturn() {
        this.$router.go(-1)
      },

      _HandleStatusMode(mode) {
        this.statusMode = mode
        this.$refs.viewer.SetStatusMode(mode)
      },

      _HandleUnfocus() {
        if (this.$refs.viewer) this.$refs.viewer.UnfocusGroup()
      },

      // Foco en un bloque (doble click): guarda el grupo y, si hay bloque, abre el panel
      // lateral para mostrar su detalle (el sidebar lo lleva a la pestaña "info").
      _HandleFocusGroup(groupId) {
        this.focusedGroupId = groupId
        if (groupId) this.showSidebar = true
      },

      _HandleToggleLod() {
        this.lodEnabled = !this.lodEnabled
        if (this.$refs.viewer) this.$refs.viewer.SetLodEnabled(this.lodEnabled)
      },

      _OnKeydown(e) {
        if (e.key !== 'Escape') return
        // Esc sale del modo foco.
        if (this.focusedGroupId) {
          this._HandleUnfocus()
          return
        }
        // Esc desarma la herramienta Vector/Texto (cierra el panel), salvo mientras se
        // escribe en un campo (el editor de texto gestiona su propio Esc).
        const tag = (document.activeElement && document.activeElement.tagName) || ''
        if ((this.isDrawing || this.selectedTool) && tag !== 'TEXTAREA' && tag !== 'INPUT') {
          this._HandleSelectTool()
        }
      },

      // Botón Selección / Esc: salir de las herramientas Vector y Texto (cierra el panel).
      _HandleSelectTool() {
        this.isDrawing = false
        this.selectedTool = null
      },

      _HandleInDesignMode(e) {
        this.designMode = e
        // El HARD RESET (salir de foco/LOD + recargar desde la fuente del modo: diseño
        // → JSON editable, vista → binario) lo hace el watcher `designMode` del visor al
        // cambiar el prop. Aquí solo sincronizamos el flag de LOD de la página (el visor
        // lo apaga) y los controles de diseño; progreso/incidencias y LOD se ocultan en
        // diseño vía la toolbar.
        if (e) this.lodEnabled = false
        // Las áreas NO se activan solas al entrar en diseño: el usuario las muestra
        // pulsando el botón de áreas (las herramientas Vector/Texto aparecen cuando
        // áreas está visible). Al salir de diseño se ocultan.
        if (!e) {
          this.showPanelViews = false
          this.showAreas = false
        }
        this.$emit('epc-in-design-mode', e)
      },

      ShowLoader(data) {
        this.isLoading = data.loading;
        this.customLoaderText = data.text
      },

      _OnLoaded(e) {
          const layers = this.$refs.viewer.GetLayers()
          layers.forEach(lyr => Vue.set(lyr, "isVisible", true))
          this.layers = layers
          this.showProgress = e.length > 0
          this.showMsgWithoutDesign = e.length == 0
          this.pictogramsInPowerPlant = e

          this.$emit('epc-photovoltaic-park-loaded', e)
      },

      _OnMounted() {
        // this._FilterLayer()
        // this._OnClickDisplayLayerPictogram(this.displayPictogramLayer)
      },

      _FilterLayer() {
        this.filterLayers = filterLayersByType(this.layers, this.displayPictogramLayer)
      },

      _OnCleared() {
          this.layers = null
      },

      _OnToggleLayer(layer, newState) {
          layer.isVisible = newState
          this.$refs.viewer.ShowLayer(layer.name, newState)
      },

      _OnToggleAll(newState) {
          if (this.layers) {
              for (const layer of this.layers) {
                  if (layer.isVisible !== newState) {
                      this._OnToggleLayer(layer, newState)
                  }
              }
          }
      },

      _OnMessage(e) {
          let type = "info"
          // switch (e.detail.level) {
          // case _DxfViewer.MessageLevel.WARN:
          //   type = "warning"
          //   break
          // case _DxfViewer.MessageLevel.ERROR:
          //   type = "negative"
          //   break
          // }
          // this.$q.notify({ type, message: e.detail.message })
      },

      _OnClickDisplayLayerPictogram(type) {
        if (type === this.displayPictogramLayer)
          this.displayPictogramLayer = this.displayDefaultPictogramLayer
        else
          this.displayPictogramLayer = type

        // mandamos a renderizar los layers filtrados por tipo
        this._FilterLayer()
        // mandamos a renderizar
        this.$refs.viewer.Render(this.displayPictogramLayer)
      },

      _onPointerDown(e) {
        console.log('_onPointerDown up: ', e.detail)
        this.$refs.viewer.GetViewer().ResaltarObject(e.detail.domEvent)
      },

      _OnClickZoom(zoomIn) {
        if(zoomIn )
          this.$refs.viewer.ZoomIn()
        else
          this.$refs.viewer.ZoomOut()
        this._RefreshZoom()
      },

      // Refresca el % del control de zoom leyendo la escala actual del viewer.
      _RefreshZoom() {
        this.$nextTick(() => {
          const level = this.$refs.viewer && this.$refs.viewer.GetZoomLevel
            ? this.$refs.viewer.GetZoomLevel()
            : 1
          this.zoomPercent = Math.round((level || 1) * 100)
        })
      },

      // TODO: definir qué muestra "Ayuda" (tour, atajos, modal de ayuda...).
      _HandleClickHelp() {
        this.$refs.helpModal.show({
          component: ViewerHelpDialog,
          title: this.$t('help'),
          width: 720,
          scrollBody: true,
        })
      },

      _OnClickFullScreen() {
        if (this.fullscreenEnabled) {
          exitFullScreen();
          this.fullscreenEnabled = false;
        } else {
          if (isFullScreenSupported()) {
            requestFullScreen(this.$refs.viewer_page);
            this.fullscreenEnabled = true;
          }
        }
      },

      _OnFitContent() {
        this.$refs.viewer.FitToScreen()
        this._RefreshZoom()
      },

      async _HandleClickSave() {
        this.$refs.viewer.Save()
      },

      // Guardado completado: salir de diseño → vista. El watcher `designMode` del visor
      // recarga desde el binario (stale tras el guardado → chip "procesando" + swap Pusher).
      _HandleSaved() {
        if (this.designMode) this._HandleInDesignMode(false)
      },

      _OnClickPan() {
        this.isDraggable = !this.isDraggable
        this.$refs.viewer.EnableDraggable(this.isDraggable)
      },

      // TODO: definir qué muestra "Información" (datos del parque, leyenda, etc.).
      // (Info ahora vive dentro del panel lateral, pestaña 'info'.)

      // Fijar/desfijar el panel lateral (chincheta): al fijar se acopla y reduce el lienzo.
      _HandlePinSidebar(pinned) {
        this.sidebarPinned = pinned
        this._ResizeViewerSoon()
      },

      _HandleCloseSidebar() {
        const wasDocked = this.sidebarPinned
        this.showSidebar = false
        this.sidebarPinned = false
        if (wasDocked) this._ResizeViewerSoon()
      },

      // Tras cambiar el layout (acoplar/soltar el panel), reajusta el stage de Konva.
      _ResizeViewerSoon() {
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.$refs.viewer && this.$refs.viewer.handleResize) this.$refs.viewer.handleResize()
          }, 60)
        })
      },

      // Candado: navegación (pan on + grupos no editables) vs edición.
      _OnToggleNavigation() {
        this.navigationMode = !this.navigationMode
        this.isDraggable = this.navigationMode
        this.$refs.viewer.SetNavigationMode(this.navigationMode)
      },

      _OnClickProgress() {
        this.datasetProject = true
        this.$emit('epc-pictogram-project-dataset', true)
      },

      _OnShowChangeStatuses(e) {
        this.showLayerToolStatuses = e.show
        this.selectedEntities = e.selected_entities
        this.statuses = e.statuses
      },

      _OnShowFormSplit(e) {
        this.showLayerToolSplit = e.show
        this.selectedEntities = e.selected_entities
      },

      _HandleChangeStatus(e) {
        // de momento una vez que cambia de estado solo completamos la acción pasando los datos a la variable que
        // contiene todos los elementos que han cambiado de estado (refierase a la estructura general no a la propiedad)
        this.$refs.viewer.CompleteAction()
      },

      handleSplitEntities(e) {
        this.$refs.viewer.SplitEntities(e)
      },

      _CancelDrawing() {
        this.isDrawing = false
        this.selectedTool = null
      },

      _CancelTextTool() {
        this.selectedTool = null
      },

      _HandleTxtEditing(data) {
        this.editingTxt = data
        if (data) {
          const style = data.fontStyle || 'normal'
          this.editingTxtFontSize = data.fontSize || 16
          this.editingTxtBold = style.includes('bold')
          this.editingTxtItalic = style.includes('italic')
          this.editingTxtColor = data.fill || '#1e1e1e'
          this.editingTxtAlign = data.align || 'left'
          this.editingTxtOpacity = data.opacity != null ? data.opacity : 1
        }
      },

      _DeleteSelectedBlock() {
        this.$refs.viewer.DeleteSelectedBlock()
        this.editingBlock = null
      },

      _DeleteSelectedTxt() {
        this.$refs.viewer.DeleteSelectedTxt()
        this.editingTxt = null
      },

      _HandleBlockEditing(data) {
        this.editingBlock = data
        if (data) {
          this.editingBlockStrokeWidth = data.strokeWidth || 0.3
          this.editingBlockDashType = dashArrayToType(data.dash)
          this.editingBlockStrokeColor = data.strokeColor || '#1e1e1e'
          this.editingBlockFillColor = data.fillColor || 'transparent'
          this.editingBlockOpacity = data.opacity != null ? data.opacity : 0.7
          this.editingBlockEdges = data.edges || 'sharp'
        }
      },

      // --- Panel de propiedades: enruta cada cambio al dibujo nuevo o al elemento
      //   seleccionado. Los watchers de editing* se encargan de llamar al motor. ---
      _OnUpdateStrokeColor(color) {
        if (this.editingBlock) this.editingBlockStrokeColor = color
        else this.drawingStrokeColor = color
      },

      _OnUpdateFillColor(color) {
        if (this.editingBlock) this.editingBlockFillColor = color
        else this.drawingFillColor = color
      },

      _OnUpdateStrokeWidth(width) {
        if (this.editingBlock) this.editingBlockStrokeWidth = width
        else this.drawingStrokeWidth = width
      },

      _OnUpdateStrokeStyle(type) {
        if (this.editingBlock) this.editingBlockDashType = type
        else this.drawingDashType = type
      },

      _OnUpdateEdges(edges) {
        if (this.editingBlock) this.editingBlockEdges = edges
        else this.drawingEdges = edges
      },

      // Capas (z-index): solo aplica sobre un elemento seleccionado (bloque o texto).
      _OnZIndex(dir) {
        if (this.editingBlock || this.editingTxt) {
          this.$refs.viewer.ChangeSelectedZIndex(dir)
        }
      },

      _OnUpdateOpacity(opacity) {
        if (this.propsMode === 'text') this.editingTxtOpacity = opacity
        else if (this.editingBlock) this.editingBlockOpacity = opacity
        else this.drawingOpacity = opacity
      },

      _OnUpdateTextColor(color) {
        this.editingTxtColor = color
      },

      _OnUpdateFontSize(size) {
        this.editingTxtFontSize = Number(size)
      },

      _OnUpdateBold(bold) {
        this.editingTxtBold = bold
      },

      _OnUpdateItalic(italic) {
        this.editingTxtItalic = italic
      },

      _OnUpdateAlign(align) {
        this.editingTxtAlign = align
      },

      _OnRequestDelete() {
        if (this.editingBlock) this._DeleteSelectedBlock()
        else if (this.editingTxt) this._DeleteSelectedTxt()
      },

      _HandleOpenPanel(namePanel) {
        if (namePanel === 'panel-views' )
          this.$refs.refModalInsertPictograms.show(this.pictograms, this.pictogramsInPowerPlant)

        if (namePanel === 'panel-drawing' ) {
          this.selectedTool = null // salir de la herramienta Texto (excluyentes)
          this.isDrawing = true
        }

        if (namePanel === 'view-pilings' || namePanel === 'view-structures' || namePanel === 'view-modules' ) {
          this.pictogramType = namePanel.replace('view-', '');
          this.showAreas = false
          // Cerrar el panel lateral al cambiar de vista, salvo que esté fijado (pin).
          if (!this.sidebarPinned) this.showSidebar = false
        }

        if (namePanel === 'view-areas' ) {
          // this.pictogramType = namePanel.replace('view-', '');
          this.showAreas = !this.showAreas
        }

        if (namePanel === 'tool-pencil') {
          this.isDrawing = false // salir del dibujo Vector (excluyentes)
          this.selectedTool = 'pencil'
        }

        // if (namePanel === 'panel-views')
        //  this.showPanelViews = true

        // if (namePanel === 'panel-layers')
        //  this.showLayerPanel = true
      },

      _AllowedAction(action) {
        if (Object.keys(this.canActionsPictogram).length === 0)
          return false;

        return this.canActionsPictogram[action] === 'undefined' ? false : this.canActionsPictogram[action]
      },

      // Visibilidad por contexto de chrome opcional (header, selector de vistas…): visible
      // salvo que el contexto lo apague explícitamente (flag === false).
      _Allowed(action) {
        return this.canActionsPictogram[action] !== false
      },

      async _HandleScreenshot() {
        try {
          this.screenshotError = null
          this.isLoading = true
          this.customLoaderText = this.$t('screenshot')
          await this.$nextTick()
          await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))

          // CaptureScreenshot gatea por headless: el bot captura el parque completo,
          // el usuario manual solo el viewport visible (respeta zoom/pan).
          let img = this.$refs.viewer.CaptureScreenshot({ includeOverlays: this.showAreas })
          // CropImage: deshabilitado porque CalculateBoundingBox ya recorta al contenido con padding,
          // el escaneo pixel-por-pixel de CropImage es redundante y costoso en imágenes grandes
          // img = await this.$refs.viewer.engine.CropImage(img)
          img = await this.$refs.viewer.engine.GetBase64Image(img, true)

          this.screenshot = img
          this.showPanelScreenshot = true
        } catch (error) {
          console.error('Error capturing screenshot:', error)
          this.screenshotError = error.message || 'unknown_error'
          this.$notify.error('error_saving')
        } finally {
          this.isLoading = false
          this.customLoaderText = null
        }
      },

      async _HandleSaveScreenshot() {
        this.saveStatusScreenshot = 'saving'

        // El thumbnail del parque SIEMPRE es el parque COMPLETO. El bot (headless) ya
        // capturó el parque completo en el preview → se guarda tal cual (sin re-capturar,
        // para no duplicar el toDataURL costoso en Lambda). En manual, el preview es el
        // viewport → re-capturamos el completo solo para guardar.
        let toSave = this.screenshot
        const isHeadless = typeof navigator !== 'undefined' && /HeadlessChrome/.test(navigator.userAgent)
        if (!isHeadless) {
          try {
            let full = this.$refs.viewer.engine.Screenshot({ includeOverlays: this.showAreas })
            toSave = await this.$refs.viewer.engine.GetBase64Image(full, true)
          } catch (error) {
            console.error('Error capturando el parque completo para guardar:', error)
          }
        }

        const success = await this.$refs.viewer.SaveScreenshot(toSave)
        this.saveStatusScreenshot = success ? 'success' : 'error'

        // Tras guardar con éxito, cerrar el panel y limpiar el preview.
        // (saveStatusScreenshot se mantiene en 'success' para el bot de capturas.)
        if (success) {
          this.showPanelScreenshot = false
          this.screenshot = null
        }
      },

      _HandleUpdateSelectedPictograms(e) {
        // e = array de ids de pictogramas a incluir en el diseño
        this.showMsgWithoutDesign = e.length == 0
        this.selectedPictograms = filterPictogramsByIds(this.pictograms, e)
        // Mantener en sync los ids del parque (son los que el modal usa para marcar
        // "Incluido en el diseño"); si no, al reabrir mostraba el estado INICIAL de la carga.
        this.pictogramsInPowerPlant = [...e]
      },

      // Eliminar un pictograma del diseño desde el botón del visor (chip del bloque): quita
      // ese id de la lista; reusa la misma lógica que el modal.
      _HandleRemovePictogram(picId) {
        const ids = this.pictogramsInPowerPlant.filter(id => id != picId)
        this._HandleUpdateSelectedPictograms(ids)
      }
    },

    created() {
      const validTypes = ['structures', 'pilings', 'modules']
      if (this.workLayer && validTypes.includes(this.workLayer)) {
        this.pictogramType = this.workLayer
      }
    },
}
</script>

<style scoped>

/* Ajustar el canvas Konva para que use 100% de altura */
:deep(.canvasContainer) {
  height: 100% !important;
}

/* Área del visor: ocupa el espacio y, al fijar el panel lateral, se encoge (flex). Todos
   los overlays del visor se posicionan relativos a ella. */
.epc-stage-area {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  overflow: hidden;
}

/* Botón "Volver" del modo foco (flotante arriba-izquierda, bajo el menú).
   Estilo sutil: fondo azul translúcido con desenfoque, en vez de la píldora blanca. */
.epc-focus-back {
  position: absolute;
  left: 10px;
  top: 58px;
  z-index: 7;
  display: flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border: 1px solid rgba(58, 107, 200, 0.35);
  border-radius: 8px;
  background-color: rgba(58, 107, 200, 0.12);
  color: #3a6bc8;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 1px 4px 0 rgba(58, 107, 200, 0.18);
  transition: background-color 0.12s ease, border-color 0.12s ease;
}

.epc-focus-back:hover {
  background-color: rgba(58, 107, 200, 0.2);
  border-color: rgba(58, 107, 200, 0.5);
}

/* Aviso "procesando nueva versión": botón redondo con spinner (arriba-derecha). */
.epc-binary-processing-btn {
  position: absolute;
  right: 10px;
  top: 54px;
  z-index: 8;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid rgba(58, 107, 200, 0.35);
  background-color: rgba(58, 107, 200, 0.12);
  color: #3a6bc8;
  cursor: pointer;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 1px 4px 0 rgba(58, 107, 200, 0.18);
  transition: background-color 0.12s ease;
}

.epc-binary-processing-btn:hover,
.epc-binary-processing-btn.open {
  background-color: rgba(58, 107, 200, 0.2);
}

.epc-binary-processing-backdrop {
  position: fixed;
  inset: 0;
  z-index: 8;
}

/* Popover con el mensaje (debajo del botón, alineado a la derecha). */
.epc-binary-processing-pop {
  position: absolute;
  right: 10px;
  top: 94px;
  z-index: 9;
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 240px;
  padding: 8px 12px;
  border: 1px solid rgba(58, 107, 200, 0.35);
  border-radius: 8px;
  background-color: #ffffff;
  color: #3a6bc8;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 4px 14px 0 rgba(0, 0, 0, 0.15);
}

/* Cabecera del visor: "Power block" + nombre de proyecto, junto al menú ☰. Estilo pro azul. */
.epc-viewer-title {
  position: absolute;
  left: 52px;
  top: 8px;
  z-index: 6;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1px;
  padding: 5px 12px;
  border-radius: 9px;
  background-color: rgba(58, 107, 200, 0.08);
  border: 1px solid rgba(58, 107, 200, 0.18);
  pointer-events: none;
  max-width: 44vw;
}

.epc-viewer-title-main {
  font-size: 14px;
  font-weight: 700;
  color: #3a6bc8;
  line-height: 1.15;
  white-space: nowrap;
}

.epc-viewer-title-sub {
  font-size: 11px;
  font-weight: 500;
  color: #8a93a3;
  line-height: 1.15;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

</style>
